import { useState, useEffect } from 'react';
import { isBefore, parseISO } from 'date-fns';
import { useFormik, type FormikErrors, type FormikValues } from 'formik';
import * as yup from 'yup';
import { Input } from 'components/UI/Inputs/Input';
import { FilterIcon } from 'components/UI/Icons';
import { Svgs, Colors } from 'environment';
import { type DateTableFilter, DateFilterOperator } from 'store/ui/tables/types';
import { InputType } from 'types/index';
import type { TableFilterProps } from '../TableFilter';
import { SelectDateOperator } from './SelectDateOperator';
import { DropdownHeader, ResetFilterIcon, DropdownTitle, Dropdown } from '../TableFilter.style';
import { Container, Inputs, Wrapper } from './DateFilter.style';
import { useTranslation } from 'hooks/store';
import { usePrevious } from 'hooks/utils';

interface Props extends TableFilterProps {
	filter: DateTableFilter;
}

export function DateFilter({
	filter,
	title,
	width,
	offset,
	isLastColumn,
	onOpen,
	onClose,
	updateFilter,
	resetFilter
}: Props) {
	const { translate } = useTranslation();
	const [operator, setOperator] = useState(filter.operator);
	const [shouldReset, setShouldReset] = useState(false);
	const [forceClose, setForceClose] = useState(false);

	function validate({ from, to }: FormikValues) {
		const errors: FormikErrors<FormikValues> = {};

		if (isBefore(parseISO(to), parseISO(from))) {
			errors.from = translate(dict => dict.tableLists.filterErrorToValue);
			errors.to = translate(dict => dict.tableLists.filterErrorToValue);
		}

		return errors;
	}

	const initialValues = {
		from: filter.value ?? filter.from ?? '',
		to: filter.to ?? ''
	};

	const validationSchema = yup.object({
		from: yup.date().required(translate(dict => dict.tableLists.filterErrorDateRequired)),
		to: yup.date().required(translate(dict => dict.tableLists.filterErrorDateRequired))
	});

	const { values, errors, touched, setFieldValue, setFieldTouched } = useFormik({
		initialValues,
		validationSchema,
		validate,
		enableReinitialize: true,
		onSubmit: () => undefined
	});

	useEffect(() => {
		if (operator !== filter.operator) setOperator(filter.operator);
	}, [filter.operator]);

	useEffect(() => {
		if (!shouldReset) return;

		if (!filter.valid && (filter.value || filter.from || filter.to)) {
			resetFilter();
			setOperator(filter.operator);
		}
		setShouldReset(false);
	}, [shouldReset]);

	const previousOperator = usePrevious(operator);
	useEffect(() => {
		if (previousOperator) {
			const from = filter.value ?? filter.from ?? '';
			const to = filter.to ?? '';
			update(from, to);
		}
	}, [operator]);

	function update(from: string, to: string) {
		if (filter.valid && shouldInvalidateFilter(filter, operator, from, to)) {
			const invalidFilter = { ...filter };
			invalidFilter.valid = false;

			updateFilter(invalidFilter);
		} else {
			const updatedFilter = parseResults(filter, operator, from, to);

			if (updatedFilter) {
				updateFilter(updatedFilter);
			}
		}
	}

	function shouldInvalidateFilter(
		filter: DateTableFilter,
		operator: DateFilterOperator,
		from: string,
		to: string
	) {
		if (!filter.value && !filter.from && !filter.to) {
			return true;
		}

		if (operator === DateFilterOperator.Between && (!from || !to)) {
			return true;
		}

		if (operator === DateFilterOperator.Exact && !from) {
			return true;
		}

		if (!from) {
			return true;
		}

		return false;
	}

	function hasFilterChanged(filter: DateTableFilter, updatedFilter: DateTableFilter) {
		return (
			filter.operator !== updatedFilter.operator ||
			(updatedFilter.value && filter.value !== updatedFilter.value) ||
			(updatedFilter.from && filter.from !== updatedFilter.from) ||
			filter.to !== updatedFilter.to
		);
	}

	function parseResults(
		filter: DateTableFilter,
		operator: DateFilterOperator,
		from: string,
		to: string
	) {
		if (!from) return;

		const updatedFilter = { ...filter };
		updatedFilter.operator = operator;
		updatedFilter.valid = true;

		if (operator === DateFilterOperator.Between) {
			if (isBefore(parseISO(from), parseISO(to))) {
				updatedFilter.value = null;

				updatedFilter.from = from;
				updatedFilter.to = to;

				if (hasFilterChanged(filter, updatedFilter)) {
					return updatedFilter;
				}
			}
		} else {
			updatedFilter.from = null;
			updatedFilter.to = null;

			updatedFilter.value = from;

			if (hasFilterChanged(filter, updatedFilter)) {
				return updatedFilter;
			}
		}
	}

	function reset() {
		if (filter.valid) resetFilter();
		onCloseDropdown();
	}

	function onCloseDropdown() {
		setShouldReset(true);
		onClose?.();
		setForceClose(true);
	}

	const isBetween = operator === DateFilterOperator.Between;

	const dropdownOffset = isLastColumn ? offset : undefined;

	return (
		<Dropdown
			usedForCustomisableTable
			width={width}
			forceClose={forceClose}
			offset={dropdownOffset}
			toggleComponent={({ ref, open, toggle }) => (
				<FilterIcon ref={ref} active={open || filter.valid} onClick={toggle} />
			)}
			setForceClose={setForceClose}
			onOpen={onOpen}
			onClose={onClose}
		>
			<>
				<DropdownHeader onClick={e => e.stopPropagation()}>
					<DropdownTitle>{title}</DropdownTitle>
					<ResetFilterIcon
						svg={Svgs.Delete}
						colors={{
							color: Colors.text.disabled,
							hover: Colors.primary.normal
						}}
						onClick={reset}
					/>
				</DropdownHeader>
				<Container onClick={e => e.stopPropagation()}>
					<SelectDateOperator selected={operator} onSelect={setOperator} />
					<Inputs>
						<Wrapper>
							<Input
								type={InputType.Date}
								value={values.from}
								error={errors.from}
								label={translate(({ filterInputs }) =>
									isBetween ? filterInputs.fromUpperCase : filterInputs.value
								)}
								onDateChange={({ formattedDate: from }) => {
									if (!touched.from) setFieldTouched('from');

									setFieldValue('from', from);
									update(from, values.to);
								}}
							/>
						</Wrapper>
						{isBetween && (
							<Wrapper>
								<Input
									type={InputType.Date}
									value={values.to}
									error={errors.to}
									label={translate(
										({ filterInputs }) => filterInputs.toUpperCase
									)}
									onDateChange={({ formattedDate: to }) => {
										if (!touched.to) setFieldTouched('to');

										setFieldValue('to', to);
										update(values.from, to);
									}}
								/>
							</Wrapper>
						)}
					</Inputs>
				</Container>
			</>
		</Dropdown>
	);
}
